import { createContext, useContext } from "react";

/**
 * Shared 1-based step numbers for the Review & Submit wizard. Kept here so the
 * layout (PageContainer) and the individual step widgets agree on the mapping.
 */
export const CHECKOUT_WIZARD_STEP = {
    ShippingMethod: 2,
    Payment: 3,
    Review: 4,
} as const;

export const CHECKOUT_WIZARD_FIRST_STEP = CHECKOUT_WIZARD_STEP.ShippingMethod;
export const CHECKOUT_WIZARD_LAST_STEP = CHECKOUT_WIZARD_STEP.Review;

/**
 * A per-step validator. Returns `true` when the step is valid. Implementations
 * are allowed to have side effects (focus/scroll to the first error, surface
 * inline error messages) so the same function can be reused when the wizard
 * jumps to an invalid step on submit.
 */
export type StepValidator = () => boolean | Promise<boolean>;

export interface SurCheckoutWizardContextValue {
    /** 1-based index of the currently active step. */
    currentStep: number;
    /** First navigable step in the wizard. */
    firstStep: number;
    /** Last navigable step in the wizard. */
    lastStep: number;
    /** True when the active step is the last step. */
    isLastStep: boolean;
    /** Navigate to a specific step (clamped to the wizard bounds). */
    goToStep: (step: number) => void;
    /** Advance one step (no validation - callers gate this themselves). */
    goToNextStep: () => void;
    /** Go back one step. */
    goToPreviousStep: () => void;
    /**
     * Register a validator for a step. Returns an unregister function so callers
     * can clean up on unmount. Steps that keep mounted (hidden) stay registered.
     */
    registerStepValidator: (step: number, validate: StepValidator) => () => void;
    /** Report the current reactive validity of a step (used to gate the Next button). */
    setStepValidity: (step: number, isValid: boolean) => void;
    /** Read the reactive validity of a step. Defaults to valid when unknown. */
    getStepValidity: (step: number) => boolean;
    /**
     * Run every registered validator in ascending step order. On the first
     * invalid step, navigate to it and return `false`. Returns `true` when all
     * steps are valid.
     */
    validateAllAndJump: () => Promise<boolean>;
}

const noop = () => {
    /* default no-op used when a consumer renders outside the provider */
};

export const defaultSurCheckoutWizardContextValue: SurCheckoutWizardContextValue = {
    currentStep: 1,
    firstStep: 1,
    lastStep: 1,
    isLastStep: true,
    goToStep: noop,
    goToNextStep: noop,
    goToPreviousStep: noop,
    registerStepValidator: () => noop,
    setStepValidity: noop,
    getStepValidity: () => true,
    validateAllAndJump: () => Promise.resolve(true),
};

export const SurCheckoutWizardContext = createContext<SurCheckoutWizardContextValue>(
    defaultSurCheckoutWizardContextValue,
);

/** Convenience hook for consuming the checkout wizard context. */
export const useSurCheckoutWizard = (): SurCheckoutWizardContextValue => useContext(SurCheckoutWizardContext);
